<template>
  <div class="report_access">

    <modal :id="report" :title="modalTitle" @shown="loadUsers">

      <div v-if="error">
        <div class="alert alert-danger">
          {{ error }}
        </div>
      </div>

      <!-- Main view mode -->
      <div class="row" v-if="viewMode === 'main'">

        <!-- Individual access -->
        <div class="col-md-6">
          <div class="ibox">

            <div class="ibox-title text-center">
              <strong>Individual access</strong>
            </div>

            <div class="ibox-content">

              <div class="mb-3">
                <user-typeahead :search-url="'reports/' + report + '/users/search'"
                                placeholder="Find and select user to add to list..."
                                @userSelected="addNewUser"></user-typeahead>
              </div>

              <div v-if="fields.users.length">

                <ul class="list-group mb-1">
                  <li v-for="(user, index) in fields.users" class="list-group-item list-group-item-text">
                    <a :href="'users/' + user.id" target="_blank">{{ user.first_name }} {{ user.last_name }}</a>
                    <button type="button" class="close" @click="removeUser(user.id)">×</button>
                  </li>
                </ul>
              </div>

              <div v-else class="text-center">
                <div v-if="usersLoading">
                  <i class="fa fa-spinner fa-spin fa-3x"></i>
                </div>
                <div v-else>
                  No one has individual access to this report.
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Role-based access -->
        <div class="col-md-6">
          <div class="ibox">

            <div class="ibox-title text-center">
              <strong>Role-based access</strong>
            </div>

            <div class="ibox-content">

              <div v-if="usersByRoleLimited.length">
                <a v-for="(user, index) in usersByRoleLimited" :href="'users/' + user.id" target="_blank"
                   @mouseover="highlightRoles(user.roles)" @mouseleave="highlightRoles()"
                   :class="getUserClass(user.id)">{{ user.first_name }} {{ user.last_name }}</a>

                <div class="text-center mt-3">
                  <a v-if="userByRoleMore" class="btn btn-xs btn-primary mr-1 mb-1" @click="viewMode = 'all-by-role'">and
                    {{ userByRoleMore }} more...</a>
                </div>
              </div>

              <div v-else class="text-center">
                <div v-if="roleBasedUsersLoading">
                  <i class="fa fa-spinner fa-spin fa-3x"></i>
                </div>
                <div v-else>
                  No one has access to this report by role.
                </div>
              </div>

            </div>

            <div class="ibox-title text-center">
              <strong>Roles With Access to the Report</strong>
            </div>

            <div class="ibox-content">
              <a v-for="(roleName, roleId) in roles" :href="'admin/roles/' + roleId + '/permissions'" target="_blank"
                 :class="getRoleClass(roleId)"
                 @mouseover="highlightUsers(roleId)" @mouseleave="highlightUsers()">{{ roleName }}</a>
            </div>

          </div>
        </div>

      </div>

      <!-- "All by role" view mode: show all role-based users without limit. -->
      <div v-if="viewMode === 'all-by-role'">
        <div class="ibox">

          <div class="ibox-title text-center">
            <strong>Role-based access</strong>
          </div>

          <div class="ibox-content">
            <div class="text-center mb-2">
              <a class="btn btn-xs btn-primary mr-1 mb-1" @click="viewMode = 'main'"><i
                  class="fa fa-arrow-circle-left"></i> back</a>
            </div>

            <div class="limited-height-container">
              <a v-for="(user, index) in usersByRole" :href="'users/' + user.id" target="_blank"
                 @mouseover="highlightRoles(user.roles)" @mouseleave="highlightRoles()"
                 :class="getUserClass(user.id)">{{ user.first_name }} {{ user.last_name }}</a>
            </div>

          </div>

          <div class="ibox-title text-center">
            <strong>Roles With Access to the Report</strong>
          </div>

          <div class="ibox-content">
            <a v-for="(roleName, roleId) in roles" :href="'admin/roles/' + roleId + '/permissions'" target="_blank"
               :class="getRoleClass(roleId)"
               @mouseover="highlightUsers(roleId)" @mouseleave="highlightUsers()">{{ roleName }}</a>
          </div>

        </div>
      </div>

      <template v-slot:footer>

        <button type="button" class="btn btn-default" @click="hideModal">Close</button>
        <button type="button" class="btn btn-primary" @click="submit" :disabled="savingNow">
          <i class="fa fa-fw fa-save"></i>Save
          <i class="fa fa-spinner fa-spin" v-if="savingNow"></i>
        </button>

      </template>

    </modal>

  </div>
</template>

<script>
import UserTypeahead from "./UserTypeahead";

export default {

  emits: ['accessGranted'],

  /**
   * Input properties.
   *
   * Usage example:
   *
   * <report-access report="coastal-bordereau-report"></report-access>
   *
   * Trigger example:
   *
   * <a href="#" class="btn btn-primary" data-toggle="modal" data-target="#coastal-bordereau-report">
   *   <i class="fa fa-fw fa-lock"></i> Access
   * </a>
   *
   * Requires window.usersWithViewReportsPermission variable to be present.
   */
  props: {

    // Modal dialog title.
    modalTitle: {
      type: String,
      default: "Report Access"
    },

    // Report machine name, e.g. "coastal-bordereau-report".
    report: {
      type: String,
      default: ""
    },

  },

  /**
   * Used components.
   */
  components: {
    UserTypeahead
  },

  /**
   * Variables (models).
   */
  data() {
    return {

      // Form fields.
      fields: {},

      // Users who have report access by role.
      usersByRole: [],

      // Roles with access to the report.
      roles: {},

      // Highlighted users/roles.
      usersHighlighted: [],
      rolesHighlighted: [],

      // Form validation error messages.
      errors: {},

      // Internal validation state.
      isValid: true,

      // Common error message.
      error: '',

      // Component view mode.
      viewMode: 'main',

      // Limit of users by role to show no "main" view mode.
      usersByRoleLimit: 20,

      // Users loading indicator.
      usersLoading: false,
      roleBasedUsersLoading: false,

      // Form is saving now indicator.
      savingNow: false,
    }
  },

  /**
   * Computed values
   */
  computed: {

    // Users who have report access by role, limited by usersByRoleLimit.
    usersByRoleLimited: function () {
      return this.usersByRole.slice(0, this.usersByRoleLimit);
    },

    // Count of users who have report access by role, over usersByRoleLimit.
    userByRoleMore: function () {
      return (this.usersByRole.length - this.usersByRoleLimited.length);
    }

  },

  /**
   * Functions (methods).
   */
  methods: {

    /**
     * Init form
     */
    init() {
      this.clearForm();
      this.fields = {
        users: [],
      };
    },

    /**
     * Submit form data.
     */
    submit() {
      let _this = this;
      this.validate();
      if (!this.isValid) return;

      this.savingNow = true;
      let formData = this.formData();
      axios.post('/reports/' + this.report + '/access', formData, formData)
          .then(response => {
            _this.$modal.hide(_this.report);
            _this.$emit('accessGranted', response.data);
          })
          .catch(error => {
            _this.handleErrors(error, 'Unable to save report access list.')
          })
          .then(() => {
            _this.savingNow = false;
          });
    },

    /**
     * Prepare form data.
     */
    formData() {
      const formData = new FormData();

      let users = this.fields.users.map(user => user.id);
      formData.append('users', users);

      /*
      console.group('FORM DATA');
      for (var pair of formData.entries()) {
          console.log(pair[0]+ ', ' + pair[1]);
      }
      console.groupEnd();
       */

      return formData;
    },

    /**
     * Validate form
     */
    validate() {
      this.errors = {};
      this.isValid = true;
    },

    /**
     * Set validation error
     */
    setError(field, message) {
      if (!this.errors[field]) this.errors[field] = [];
      this.errors[field].push(message);
      this.isValid = false;
    },

    /**
     * Handle axios errors
     *
     * @param errorObj
     * @param defaultMessage
     */
    handleErrors(errorObj, defaultMessage) {
      if (errorObj.response.status === 422) {
        this.errors = errorObj.response.data.errors || {};
      } else {
        this.error = defaultMessage;
      }
    },

    /**
     * Clear form fields and messages
     */
    clearForm() {
      this.fields = {};
      this.errors = {};
      this.error = '';
    },

    /**
     * Load users.
     */
    loadUsers() {

      if (!this.report) return;

      this.init();

      let _this = this;

      // Load users with individual access.
      _this.usersLoading = true;
      axios.get('/reports/' + _this.report + '/users/access/individual')
          .then(response => {
            _this.fields.users = response.data;
            _this.sortUsers();
          })
          .catch(error => {
            _this.handleErrors(error, 'Unable to load users.')
          })
          .then(() => {
            _this.usersLoading = false;
          });

      // Load users with role-based access.
      _this.roleBasedUsersLoading = true;
      axios.get('/reports/' + _this.report + '/users/access/role')
          .then(response => {
            _this.usersByRole = response.data.users;
            _this.roles = response.data.roles;
          })
          .catch(error => {
            _this.handleErrors(error, 'Unable to load users by role.')
          })
          .then(() => {
            _this.roleBasedUsersLoading = false;
          });
    },

    /**
     * Remove user from individual report access list.
     */
    removeUser(userId) {
      let userIndex = this.fields.users.findIndex(user => user.id === userId);
      this.fields.users.splice(userIndex, 1);
    },

    /**
     * Add user to individual report access list.
     */
    addNewUser(newUser) {
      let userIndex = this.fields.users.findIndex(user => user.id === newUser.id);
      if (userIndex === -1) {
        this.fields.users.push(newUser);
      }
      this.sortUsers();
    },

    /**
     * Sort users with individual access.
     */
    sortUsers() {
      this.fields.users.sort((a, b) => (a.last_name > b.last_name) ? 1 : -1);
    },

    /**
     * Highlight user roles on hover.
     */
    highlightRoles(roleIds) {
      this.rolesHighlighted = roleIds ? (roleIds.length ? roleIds : []) : [];
    },

    /**
     * Generate role class.
     */
    getRoleClass(roleId) {
      return 'btn btn-xs mr-1 mb-1 ' + (this.rolesHighlighted.indexOf(parseInt(roleId)) > -1 ? 'btn-success' : 'btn-default');
    },

    /**
     * Highlight users on role hover.
     */
    highlightUsers(roleId) {
      if (roleId) {
        this.usersHighlighted = this.usersByRole
            .filter((user) => {
              return user.roles.indexOf(parseInt(roleId)) > -1;
            })
            .map((user) => {
              return user.id;
            });
      } else {
        this.usersHighlighted = [];
      }
    },

    /**
     * Generate user class.
     */
    getUserClass(userId) {
      return 'btn btn-xs mr-1 mb-1 ' + (this.usersHighlighted.indexOf(parseInt(userId)) > -1 ? 'btn-success' : 'btn-default');
    },

    hideModal() {
      this.$modal.hide(this.report);
    },

  },

  /**
   * Initialize component on create.
   */
  created() {
    this.init();
  },

  /**
   * Initialize component on mount.
   */
  mounted() {
    // Load users with report access on modal opening.
    this.loadUsers();
  }

}
</script>

<style scoped>
.limited-height-container {
  max-height: 225px;
  overflow: auto;
}
</style>
